import React, { useEffect, useState } from "react";
import { Calendar, MapPin, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { eventInfo, heroImage, natureImage } from "../data/mock";

function useCountdown(target) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = new Date(target).getTime() - Date.now();
      if (diff <= 0) return setT({ d: 0, h: 0, m: 0, s: 0 });
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
}

const TimerCircle = ({ value, label, color, ring }) => (
  <div className="flex flex-col items-center">
    <div
      className="relative w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center"
      style={{ background: ring }}
    >
      <div className="absolute inset-1.5 bg-white rounded-full flex flex-col items-center justify-center shadow-inner">
        <div className="text-[10px] tracking-[0.25em] font-bold" style={{ color }}>
          {label}
        </div>
        <div className="font-display text-3xl md:text-4xl text-[#0f2a20] leading-none mt-1">
          {String(value).padStart(2, "0")}
        </div>
      </div>
    </div>
  </div>
);

export default function Hero() {
  const t = useCountdown(eventInfo.targetDate);

  return (
    <section id="home" className="relative pt-28 pb-20 overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 -left-20 w-96 h-96 rounded-full bg-[#1F5943]/8 blur-3xl" />
        <div className="absolute top-40 right-0 w-[500px] h-[500px] rounded-full bg-[#F59E0B]/8 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left */}
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1F5943]/10 border border-[#1F5943]/20 text-[#1F5943] mb-6">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs font-semibold tracking-widest uppercase">
                Edition 2026 &middot; Life Sciences
              </span>
            </div>

            <h1 className="font-display text-[#C4433D] text-lg md:text-xl tracking-wider uppercase mb-4">
              Do we have another Earth to go to?
            </h1>

            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-[#0f2a20] mb-4">
              Sustainability
              <br />
              Leader
              <br />
              <span className="text-[#1F5943]">Awards.</span>
            </h2>

            <p className="text-[#3a5148] text-lg max-w-xl leading-relaxed mb-8">
              Recognising biotech and life sciences labs building a net-zero,
              circular, biodiversity-positive future. Nominations open now.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <a href="#register">
                <Button className="bg-[#1F5943] hover:bg-[#164031] text-white rounded-full px-8 h-14 text-base font-semibold shadow-lg hover:shadow-xl transition-all group">
                  Submit Your Nomination
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a href="#priorities">
                <Button
                  variant="outline"
                  className="rounded-full px-8 h-14 text-base font-semibold border-2 border-[#1F5943] text-[#1F5943] hover:bg-[#1F5943] hover:text-white transition-all"
                >
                  Explore Priorities
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white border border-[#1F5943]/15 shadow-sm flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#1F5943]" />
                </div>
                <div>
                  <div className="text-xs text-[#7a8b83] font-semibold uppercase tracking-wider">
                    Date
                  </div>
                  <div className="font-semibold text-[#0f2a20]">
                    {eventInfo.date} &middot; {eventInfo.day}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white border border-[#1F5943]/15 shadow-sm flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#C4433D]" />
                </div>
                <div>
                  <div className="text-xs text-[#7a8b83] font-semibold uppercase tracking-wider">
                    Location
                  </div>
                  <div className="font-semibold text-[#0f2a20]">{eventInfo.location}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: layered images */}
          <div className="lg:col-span-5 relative h-[520px] hidden lg:block">
            <div className="absolute top-0 right-0 w-72 h-96 rounded-[40%_60%_60%_40%/50%_50%_50%_50%] overflow-hidden shadow-2xl float-anim">
              <img
                src={natureImage}
                alt="Nature"
                className="w-full h-full object-cover"
              />
            </div>
            <div
              className="absolute bottom-8 left-0 w-64 h-72 rounded-[60%_40%_40%_60%/40%_60%_40%_60%] overflow-hidden shadow-2xl float-anim"
              style={{ animationDelay: "1.5s" }}
            >
              <img
                src={heroImage}
                alt="Renewable"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute top-24 left-10 bg-white rounded-2xl shadow-xl px-4 py-3 border border-[#1F5943]/10 float-anim" style={{animationDelay:'0.7s'}}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                <div className="text-xs font-semibold text-[#0f2a20]">Nominations Live</div>
              </div>
            </div>
            <div className="absolute bottom-40 right-8 bg-white rounded-2xl shadow-xl px-4 py-3 border border-[#1F5943]/10 float-anim" style={{animationDelay:'2.2s'}}>
              <div className="text-xs text-[#7a8b83] font-semibold">SDG Aligned</div>
              <div className="font-display text-lg text-[#1F5943]">17 Goals</div>
            </div>
          </div>
        </div>

        {/* Countdown */}
        <div className="mt-20">
          <div className="text-center mb-6">
            <div className="text-xs tracking-[0.3em] font-bold text-[#7a8b83] uppercase">
              Nominations close in
            </div>
          </div>
          <div className="flex justify-center items-center gap-4 md:gap-8 flex-wrap">
            <TimerCircle value={t.d} label="DAYS" color="#F59E0B" ring="conic-gradient(#F59E0B 65%, #FEF3C7 65%)" />
            <TimerCircle value={t.h} label="HOURS" color="#3B82F6" ring="conic-gradient(#3B82F6 40%, #DBEAFE 40%)" />
            <TimerCircle value={t.m} label="MINUTES" color="#10B981" ring="conic-gradient(#10B981 75%, #D1FAE5 75%)" />
            <TimerCircle value={t.s} label="SECONDS" color="#EF4444" ring="conic-gradient(#EF4444 55%, #FEE2E2 55%)" />
          </div>
        </div>
      </div>
    </section>
  );
}
